// src/services/firebase/index.ts
import {
  getAuth,
  signInWithRedirect,
  signOut,
  GoogleAuthProvider,
  onAuthStateChanged,
  User,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  ActionCodeSettings,
  browserLocalPersistence,
  setPersistence,
} from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  collection,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
} from "firebase/firestore";
import { initializeApp } from "firebase/app";
// Type-only: erased at compile time, so `firebase/analytics` stays out of the
// main chunk. The implementation is dynamically imported after startup below.
import type { Analytics } from "firebase/analytics";
import { emailSignInUrl } from "../../utils/emailSignInUrl";

// Initialize Firebase with environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || "",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "",
};

// Validate required config. When anything is missing, main.tsx renders a
// message naming the vars instead of the app.
const requiredFields = ["apiKey", "authDomain", "projectId", "appId"] as const;
export const missingFirebaseVars = requiredFields
  .filter((f) => !firebaseConfig[f])
  .map((f) => `VITE_FIREBASE_${f.replace(/([A-Z])/g, "_$1").toUpperCase()}`);
export const firebaseConfigValid = missingFirebaseVars.length === 0;
if (!firebaseConfigValid) {
  console.error(
    "Firebase config is incomplete — the following env vars are missing:",
    missingFirebaseVars
  );
}

// Unassigned when the config is incomplete: `getAuth` throws on an empty API
// key, and a throw here would stop main.tsx from rendering anything. Nothing
// reads them in that case because the app itself never mounts.
let app!: ReturnType<typeof initializeApp>;
let auth!: ReturnType<typeof getAuth>;
let db!: ReturnType<typeof getFirestore>;
let analytics: Analytics | undefined;

if (firebaseConfigValid) {
  try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    // The SDK's own IndexedDB cache replaces the hand-rolled localStorage
    // copies the dashboard and the to-do widget used to keep: snapshots fire
    // from cache before the network answers (instant first paint), writes made
    // offline are queued and replayed, and tabs stay consistent with each
    // other. If IndexedDB is unavailable (private windows, blocked storage)
    // the SDK falls back to its memory cache on its own.
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  } catch (e) {
    console.error("Firebase failed to initialize:", e);
    // Create minimal stubs so the rest of the module doesn't throw on import.
    app = initializeApp(firebaseConfig, "[fallback]");
    auth = getAuth(app);
    db = getFirestore(app);
  }
}

// Analytics is not needed to render anything, and pulling it in at module scope
// put @firebase/analytics + installations on the critical path and raced gtag.js
// against app startup. Load it once the browser is idle instead.
function initAnalyticsWhenIdle() {
  if (typeof window === "undefined") return;
  const start = () => {
    import("firebase/analytics")
      .then(({ getAnalytics, isSupported }) =>
        isSupported().then((ok) => {
          if (ok) analytics = getAnalytics(app);
        })
      )
      .catch((e) => console.warn("Firebase Analytics failed to initialize:", e));
  };
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(start, { timeout: 5000 });
  } else {
    setTimeout(start, 3000);
  }
}

if (firebaseConfigValid) initAnalyticsWhenIdle();

// To use the Firebase Auth/Firestore emulators for local development in the future:
// Uncomment the following block and set the desired localStorage flag as needed.
// if (
//   window.location.hostname === 'localhost' &&
//   window.localStorage.getItem('useProdAuth') !== 'true'
// ) {
//   console.log('Using auth emulator');
//   connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
//   connectFirestoreEmulator(db, 'localhost', 8080);
// }

// Set persistence to LOCAL
if (firebaseConfigValid) {
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.error("Error setting persistence:", error);
  });
}

const actionCodeSettings: ActionCodeSettings = {
  url: emailSignInUrl(window.location.origin),
  handleCodeInApp: true,
};

// Types
export interface UrlData {
  id: string;
  url: string;
  name?: string;
  description?: string; // optional description for UI
  order: number;
  col: number; // column assignment for dashboard DnD
  tags?: string[];
  createdDate?: number;
  bgColor?: string; // background color for card UI
  isAd?: boolean; // Indicates if this is an ad link
}

export interface ScratchpadData {
  text: string;
  timestamp: Date;
}

export interface WeatherWidgetStatus {
  enabled: boolean;
  lastUpdated: Date;
}

// Auth Service
export const authService = {
  currentUser: () => auth.currentUser,

  onAuthStateChanged: (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, callback);
  },

  signInWithGoogle: async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    return signInWithRedirect(auth, provider);
  },

  signOut: () => signOut(auth),
};

// Passwordless Auth Service
export const passwordlessAuthService = {
  sendSignInLink: async (email: string) => {
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    window.localStorage.setItem("emailForSignIn", email);
    return true;
  },

  signInWithEmailLink: async (email: string, emailLink: string) => {
    if (!isSignInWithEmailLink(auth, emailLink)) {
      throw new Error("Invalid sign-in link");
    }
    return await signInWithEmailLink(auth, email, emailLink);
  },
};

// URL Service
export const urlService = {
  // Subscribe to URL changes
  subscribeToUrls: (userId: string, callback: (urls: UrlData[]) => void) => {
    const urlsRef = collection(db, `users/${userId}/urls`);
    const q = query(urlsRef, orderBy("order", "asc"));

    return onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
      const urls = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as UrlData[];
      callback(urls);
    });
  },

  // Add new URL
  addUrl: async (userId: string, urlData: Omit<UrlData, "id">) => {
    const urlsRef = collection(db, `users/${userId}/urls`);
    const newDoc = doc(urlsRef);
    await setDoc(newDoc, urlData);
    return newDoc.id;
  },

  // Update URL
  updateUrl: async (userId: string, urlId: string, urlData: Partial<UrlData>) => {
    const urlRef = doc(db, `users/${userId}/urls/${urlId}`);
    await setDoc(urlRef, urlData, { merge: true });
  },

  // Delete URL
  deleteUrl: async (userId: string, urlId: string) => {
    const urlRef = doc(db, `users/${userId}/urls/${urlId}`);
    await deleteDoc(urlRef);
  },

  // Update URL order
  updateUrlOrder: async (userId: string, urlId: string, newOrder: number) => {
    const urlRef = doc(db, `users/${userId}/urls/${urlId}`);
    await setDoc(urlRef, { order: newOrder }, { merge: true });
  },
};

// Scratchpad Service
export const scratchpadService = {
  // Get scratchpad content
  getScratchpad: async (userId: string): Promise<ScratchpadData | null> => {
    const scratchpadRef = doc(db, `users/${userId}/notes/scratchpad`);
    const docSnap = await getDoc(scratchpadRef);
    return docSnap.exists() ? (docSnap.data() as ScratchpadData) : null;
  },

  // Update scratchpad
  updateScratchpad: async (userId: string, text: string) => {
    const scratchpadRef = doc(db, `users/${userId}/notes/scratchpad`);
    await setDoc(scratchpadRef, {
      text,
      timestamp: new Date(),
    });
  },

  // Clear scratchpad
  clearScratchpad: async (userId: string) => {
    const scratchpadRef = doc(db, `users/${userId}/notes/scratchpad`);
    await deleteDoc(scratchpadRef);
  },
};

// Weather Widget Service
export const weatherWidgetService = {
  // Get widget status
  getWidgetStatus: async (userId: string): Promise<WeatherWidgetStatus | null> => {
    const userRef = doc(db, `users/${userId}`);
    const docSnap = await getDoc(userRef);
    return docSnap.exists()
      ? {
          enabled: docSnap.data().weatherWidgetStatus || false,
          lastUpdated: docSnap.data().weatherWidgetLastUpdated?.toDate() || new Date(),
        }
      : null;
  },

  // Update widget status
  updateWidgetStatus: async (userId: string, enabled: boolean) => {
    const userRef = doc(db, `users/${userId}`);
    await setDoc(
      userRef,
      {
        weatherWidgetStatus: enabled,
        weatherWidgetLastUpdated: new Date(),
      },
      { merge: true }
    );
  },
};

export { auth, db, analytics };
